// useGuidedSegments.ts — the Viewer's guided-segmentation controller. While
// the Segmentation card is in Guided mode it owns the draft guide (seeded
// from the scan's stored guide), which segment taps on the model go to, and
// the cloud the model card shows (segments tinted, seeds as marker splats —
// viewer/guide.ts). Taps add a seed to the current target, or remove the
// seed whose marker was tapped. The 3D box tool (splat/boxGizmo.ts) works on
// the target segment: armed with "Box", a drag across the sheet draws its
// box (height fitted to what stands there); afterwards its face handles
// resize it and its centre handle moves it. Mid-drag boxes live only in the
// overlay (liveBox) and land in the draft on release, so the display cloud
// isn't rebuilt on every pointer move. Apply POSTs the draft (mode "guided"),
// refreshes the scan so its stored guide follows, and re-fetches the
// per-gaussian assignment (GET /segments?ids=1) for the tint.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import type { DimensionsResult, Guide, Scan, SegmentsResult } from '../api';
import type { OverlayBox, ViewCamera } from '../components/SplatView';
import { errorMessage } from '../format';
import {
  DEFAULT_BOX_HEIGHT_M,
  axisParam,
  fitHeight,
  footprintBox,
  handlePoint,
  hitHandle,
  moveFace,
  rayPlaneZ,
  translateBox,
  unitAxis,
} from '../splat/boxGizmo';
import type { Box3 } from '../splat/boxGizmo';
import { mat4Multiply } from '../splat/math';
import type { GrabHandler } from '../splat/orbit';
import { rayFromScreen } from '../splat/pick';
import type { SplatCloud } from '../splat/format';
import type { PickHit } from '../splat/pick';
import {
  addSeed,
  addSegment,
  clearSeeds,
  describeTarget,
  draftFromGuide,
  guideProblem,
  guidedDisplayCloud,
  markerSeed,
  PLANE_TOL_M,
  removeSeed,
  removeSegment,
  renameSegment,
  sameGuide,
  segmentBox,
  segmentColor,
  setSegmentBox,
  toWire,
  undoSeed,
} from './guide';
import type { SeedTarget } from './guide';
import type { TapTool } from './ModelCard';

export interface GuidedSegments {
  active: boolean;
  setActive: (on: boolean) => void;
  draft: Guide;
  target: SeedTarget;
  setTarget: (t: SeedTarget) => void;
  result: SegmentsResult | null; // the stored guide on the current cloud
  busy: boolean;
  notice: string;
  dirty: boolean; // draft differs from the stored guide
  problem: string | null; // why Apply is disabled
  displayCloud: SplatCloud | null;
  tapTool: TapTool | null;
  add: () => void;
  remove: (id: number) => void;
  rename: (id: number, name: string) => void;
  undo: () => void;
  clear: () => void;
  drawingBox: number | null; // segment id whose box the next sheet drag draws
  drawBox: (id: number) => void; // arm (or disarm) drawing segment id's box
  removeBox: (id: number) => void;
  revert: () => void;
  apply: () => Promise<void>;
}

export interface GuidedSegmentsDeps {
  scan: Scan | null;
  cloud: SplatCloud | null;
  onScan: (s: Scan) => void;
  onSegmented: (dims: DimensionsResult | null) => void;
  onError: (msg: string) => void;
}

const firstTarget = (g: Guide): SeedTarget => (g.segments.length ? { kind: 'segment', id: g.segments[0].id } : { kind: 'exclude' });

export function useGuidedSegments({ scan, cloud, onScan, onSegmented, onError }: GuidedSegmentsDeps): GuidedSegments {
  const stored = scan?.guide ?? null;
  const [active, setActive] = useState(false);
  const [draft, setDraft] = useState<Guide>(() => draftFromGuide(stored));
  const [target, setTarget] = useState<SeedTarget>(() => firstTarget(draftFromGuide(stored)));
  const [result, setResult] = useState<SegmentsResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [drawingBox, setDrawingBox] = useState<number | null>(null);
  const [liveBox, setLiveBox] = useState<{ id: number; box: Box3 } | null>(null);

  // A new scan, or a stored guide that changed underneath an untouched
  // draft (our own Apply, or an MCP client's), resets the draft.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const prevStored = useRef<{ id: string | undefined; guide: Guide | null }>({ id: scan?.id, guide: stored });
  const storedKey = JSON.stringify(stored);
  useEffect(() => {
    const prev = prevStored.current;
    const untouched = sameGuide(draftRef.current, prev.guide) || sameGuide(draftRef.current, draftFromGuide(prev.guide));
    if (prev.id !== scan?.id || untouched) {
      const next = draftFromGuide(stored);
      console.info(`[openscan/guided] draft reset from the stored guide (${next.segments.length} segments)`);
      setDraft(next);
      setTarget(firstTarget(next));
    }
    prevStored.current = { id: scan?.id, guide: stored };
    // storedKey stands in for `stored` (a fresh object on every scan update).
  }, [scan?.id, storedKey]);

  // Keep the target pointing at a segment that exists; drawing follows it.
  useEffect(() => {
    if (target.kind === 'segment' && !draft.segments.some((s) => s.id === target.id)) setTarget(firstTarget(draft));
  }, [draft, target]);
  useEffect(() => {
    if (drawingBox !== null && (target.kind !== 'segment' || target.id !== drawingBox)) setDrawingBox(null);
  }, [target, drawingBox]);

  // The stored guide's assignment on the current cloud, for the tint.
  const scanId = scan?.id ?? '';
  const status = scan?.status;
  const cloudCount = cloud?.count ?? 0;
  useEffect(() => {
    if (!active || !scanId || !stored || status === 'refining') {
      if (!stored) setResult(null);
      return;
    }
    let cancelled = false;
    api
      .getSegments(scanId, { ids: true, dimensions: false })
      .then((r) => {
        if (cancelled) return;
        console.info(`[openscan/guided] ${scanId}: ${r.segments.map((s) => `${s.name}=${s.count}`).join(', ')} (${r.source})`);
        setResult(r);
      })
      .catch((err) => {
        if (!cancelled) console.warn('[openscan/guided] segments unavailable:', errorMessage(err));
      });
    return () => {
      cancelled = true;
    };
    // stored is keyed by storedKey (see above).
  }, [active, scanId, storedKey, status, cloudCount]);

  const displayCloud = useMemo(() => (active && cloud ? guidedDisplayCloud(cloud, draft, result) : cloud), [active, cloud, draft, result]);

  const onPick = useCallback(
    (hit: PickHit | null) => {
      if (!hit) {
        setNotice('No surface under that tap — try again on the object.');
        return;
      }
      const g = draftRef.current;
      const marker = markerSeed(g, hit.id);
      if (marker) {
        console.info('[openscan/guided] removing seed', marker);
        setDraft(removeSeed(g, marker.target, marker.index));
        setNotice(`Removed a ${describeTarget(g, marker.target)} point.`);
        return;
      }
      const r = addSeed(g, target, hit.point);
      if ('error' in r) {
        setNotice(r.error);
        return;
      }
      console.info('[openscan/guided] seed', { target, point: hit.point, gaussian: hit.id });
      setDraft(r.guide);
      setNotice(`Added a ${describeTarget(g, target)} point. Tap a point again to remove it.`);
    },
    [target],
  );

  const commitBox = useCallback((id: number, b: Box3, msg: string) => {
    console.info('[openscan/guided] box', { id, min: b.min, max: b.max });
    setDraft((d) => setSegmentBox(d, id, b));
    setLiveBox(null);
    setNotice(msg);
  }, []);

  // onGrab claims a press on the target segment's box handles, or, while
  // drawing is armed, anywhere the press ray meets the sheet.
  const onGrab = useCallback(
    (x: number, y: number, cam: ViewCamera): GrabHandler | null => {
      if (target.kind !== 'segment') return null;
      const id = target.id;
      const g = draftRef.current;
      const name = describeTarget(g, target);
      const rayAt = (px: number, py: number) => rayFromScreen(cam.view, cam.proj, px, py, cam.width, cam.height);
      const r0 = rayAt(x, y);
      if (!r0) return null;
      const box = segmentBox(g, id);
      const h = box ? hitHandle(box, mat4Multiply(cam.proj, cam.view), cam.width, cam.height, x, y) : null;
      if (box && h?.kind === 'face') {
        const origin = handlePoint(box, h), axis = unitAxis(h.axis);
        const t0 = axisParam(r0, origin, axis);
        if (t0 === null) {
          setNotice('That face points straight at you — turn the view to drag it.');
          return null;
        }
        let cur: Box3 = box;
        return {
          move: (mx, my) => {
            const r = rayAt(mx, my);
            const t = r ? axisParam(r, origin, axis) : null;
            if (t === null) return;
            cur = moveFace(box, h.axis, h.side, t - t0);
            setLiveBox({ id, box: cur });
          },
          end: () => (cur === box ? setLiveBox(null) : commitBox(id, cur, `Resized the ${name} box.`)),
        };
      }
      if (box && h?.kind === 'move') {
        const z = (box.min[2] + box.max[2]) / 2;
        const p0 = rayPlaneZ(r0, z);
        if (!p0) return null;
        let cur: Box3 = box;
        return {
          move: (mx, my) => {
            const r = rayAt(mx, my);
            const p = r ? rayPlaneZ(r, z) : null;
            if (!p) return;
            cur = translateBox(box, [p[0] - p0[0], p[1] - p0[1], 0]);
            setLiveBox({ id, box: cur });
          },
          end: () => (cur === box ? setLiveBox(null) : commitBox(id, cur, `Moved the ${name} box.`)),
        };
      }
      if (drawingBox !== id || !cloud) return null;
      const a = rayPlaneZ(r0, 0);
      if (!a) return null;
      let cur: Box3 | null = null;
      return {
        move: (mx, my) => {
          const r = rayAt(mx, my);
          const b = r ? rayPlaneZ(r, 0) : null;
          if (!b) return;
          const fp = footprintBox(a, b, 0);
          const top = fitHeight(cloud, fp.min[0], fp.max[0], fp.min[1], fp.max[1], PLANE_TOL_M) ?? DEFAULT_BOX_HEIGHT_M;
          cur = footprintBox(a, b, top);
          setLiveBox({ id, box: cur });
        },
        end: () => {
          if (!cur || cur.max[0] - cur.min[0] < 0.003 || cur.max[1] - cur.min[1] < 0.003) {
            setLiveBox(null);
            setNotice('Drag across the table from one corner of the object to the opposite one.');
            return;
          }
          setDrawingBox(null);
          commitBox(id, cur, `Drew a box for ${name}. Drag its handles to fit, then apply.`);
        },
      };
    },
    [target, drawingBox, cloud, commitBox],
  );

  // The overlay: every segment's box, the target's with handles, a
  // mid-drag box in place of its stored one.
  const boxes = useMemo<OverlayBox[]>(() => {
    const out: OverlayBox[] = [];
    draft.segments.forEach((s, i) => {
      const b = liveBox?.id === s.id ? liveBox.box : s.box;
      if (b) out.push({ min: b.min, max: b.max, color: segmentColor(i), handles: target.kind === 'segment' && target.id === s.id });
    });
    return out;
  }, [draft, liveBox, target]);

  const tapTool = useMemo<TapTool | null>(() => {
    if (!active) return null;
    const name = describeTarget(draft, target);
    let hint = `Tap to add ${name} points`;
    if (target.kind === 'segment' && drawingBox === target.id) hint = `Drag across the table around the object to draw the ${name} box`;
    else if (target.kind === 'segment' && segmentBox(draft, target.id)) hint = `Drag the ${name} box's dots to resize it, its square to move it; tap to add points`;
    return { hint, onPick, boxes, onGrab };
  }, [active, draft, target, drawingBox, onPick, boxes, onGrab]);

  const apply = async (): Promise<void> => {
    if (!scan || guideProblem(draft)) return;
    setBusy(true);
    setNotice('Segmenting…');
    try {
      const r = await api.segmentGuided(scan.id, toWire(draft));
      const counts = r.segments.segments.map((s) => `${s.name} ${s.count.toLocaleString()}`).join(' · ');
      const missed = r.segments.unmatched.length;
      console.info('[openscan/guided] applied', r.stats, r.segments.segments.map((s) => [s.id, s.count]));
      setNotice(missed ? `${counts}. ${missed} point${missed === 1 ? '' : 's'} hit nothing and were ignored.` : counts);
      onSegmented(r.dimensions);
      onScan(await api.getScan(scan.id));
    } catch (err) {
      setNotice('');
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return {
    active,
    setActive,
    draft,
    target,
    setTarget,
    result,
    busy,
    notice,
    dirty: !sameGuide(draft, stored),
    problem: guideProblem(draft),
    displayCloud,
    tapTool,
    add: () => {
      const r = addSegment(draft);
      setDraft(r.guide);
      setTarget({ kind: 'segment', id: r.id });
    },
    remove: (id) => setDraft(removeSegment(draft, id)),
    rename: (id, name) => setDraft(renameSegment(draft, id, name)),
    undo: () => setDraft(undoSeed(draft, target)),
    clear: () => setDraft(clearSeeds(draft)),
    drawingBox,
    drawBox: (id) => {
      setTarget({ kind: 'segment', id });
      setDrawingBox((prev) => (prev === id ? null : id));
      setNotice('');
    },
    removeBox: (id) => {
      setDraft(setSegmentBox(draft, id, null));
      if (drawingBox === id) setDrawingBox(null);
    },
    revert: () => {
      setDraft(draftFromGuide(stored));
      setNotice('');
    },
    apply,
  };
}
